/**
 * OrganizationStep — second step of the onboarding wizard.
 *
 * Creates a self-serve Organization via POST /api/v1/organizations.
 * Pre-fills the name for custom-domain emails using extractOrgNameFromEmail,
 * and leaves the field blank for freemail users.
 */

import { useEffect, useRef, useState, useMemo, type FormEvent, type RefObject } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useOrganizationList } from '@clerk/react'
import { extractOrgNameFromEmail } from '@spoofio/shared'
import { api, ApiError } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import { Button, Input } from '@/components/ui'
import { useAutoFocus } from '@/hooks/useAutoFocus'

type OrganizationStepProps = {
  onCreated: (org: { id: string; name: string; slug: string }) => void
  headingRef?: RefObject<HTMLHeadingElement>
}

const MIN_NAME = 2
const MAX_NAME = 100

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
}

function mapCreateError(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong. Please try again.'
  if (error.status === 409) return 'That organization name is already taken. Please choose another.'
  if (error.status === 422) return error.message || 'Please check the organization name and try again.'
  return error.message || 'Unable to create organization. Please try again.'
}

export function OrganizationStep({ onCreated, headingRef }: OrganizationStepProps) {
  const { email } = useAuth()
  const { userMemberships } = useOrganizationList({ userMemberships: true })

  // Priority: Clerk org name (entered at signup) > email-domain extraction > blank
  const suggested = useMemo(() => {
    const clerkOrgName = userMemberships.data?.[0]?.organization?.name ?? null
    if (clerkOrgName) return clerkOrgName
    return email ? extractOrgNameFromEmail(email) : null
  }, [userMemberships.data, email])

  const [name, setName] = useState<string>(suggested ?? '')
  const [userEdited, setUserEdited] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // When Clerk org list loads async, fill the input if user hasn't typed yet
  useEffect(() => {
    if (suggested && !userEdited && !name) {
      setName(suggested)
    }
  }, [suggested, userEdited, name])
  const nameInputRef = useRef<HTMLInputElement>(null)
  useAutoFocus(nameInputRef)

  const slug = useMemo(() => slugify(name.trim()), [name])

  const mutation = useMutation({
    mutationFn: (payload: { name: string; slug: string }) =>
      api.createOrganization(payload),
    onSuccess: (org) => {
      onCreated({ id: org.id, name: org.name, slug: org.slug })
    },
    onError: (err) => {
      setError(mapCreateError(err))
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmed = name.trim()
    if (trimmed.length < MIN_NAME || trimmed.length > MAX_NAME) {
      setError(`Name must be between ${MIN_NAME} and ${MAX_NAME} characters.`)
      return
    }
    if (!slug) {
      setError('Please use letters or numbers in your organization name.')
      return
    }
    mutation.mutate({ name: trimmed, slug })
  }

  return (
    <form
      onSubmit={handleSubmit}
      role="form"
      aria-label="Create your organization"
      className="max-w-xl mx-auto space-y-8 py-8"
    >
      <div className="space-y-2">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl font-bold text-gray-900 focus:outline-none"
        >
          Name your organization
        </h2>
        <p className="text-gray-600">
          This is how your organization appears in Spoofio.
        </p>
      </div>

      <Input
        ref={nameInputRef}
        name="organization-name"
        label="Organization name"
        placeholder="Your company or project name"
        value={name}
        onChange={(e) => { setName(e.target.value); setUserEdited(true); }}
        error={error ?? undefined}
        className="w-full"
      />

      {suggested && (
        <p className="text-sm text-gray-500">
          We pre-filled this from your signup. You can change it.
        </p>
      )}

      {error && (
        <div role="alert" className="bg-danger-50 text-danger-700 p-4 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          size="lg"
          isLoading={mutation.isPending}
          className="w-full sm:w-auto min-h-[44px]"
        >
          Continue
        </Button>
      </div>
    </form>
  )
}
